
import { defineComponent } from 'vue';
import { mapActions } from 'pinia';
import type { Route } from 'vue-router';

// Helpers
import { getAppVariable } from '@white-label-helper/get-app-variable';
import { getFeatureFlag } from '@white-label-plugin/launch-darkly';
import { getAppHeroProduct } from '@white-label-helper/get-app-hero-product';
import { isLoungePortal } from '@white-label-helper/shared-helpers';
import { useScreenSize } from '@white-label-helper/shared-composables';

// Constants
import { COLOUR_SCHEMES, FEATURE_FLAGS } from '@white-label-configuration/constants';

import { useSearchResultsStore } from '@white-label-store/search-results';

type ColourScheme = (typeof COLOUR_SCHEMES)[keyof typeof COLOUR_SCHEMES];

type State = 'undone' | 'done' | 'in-progress' | 'blocked';

type Step = {
  routeName: string;
  state: State;
};

export default defineComponent({
  name: 'ProgressIndicator',

  components: {
    IconCheck: () => import('@white-label-icon/icon-check'),
  },

  setup() {
    const { isDesktopResolution } = useScreenSize();
    return { isDesktopResolution };
  },

  props: {
    skipExtras: {
      type: Boolean,
    },

    homeRoute: {
      type: String,
      default: '/',
    },

    searchRoute: {
      type: String,
      default: '/',
    },

    extrasRoute: {
      type: String,
      default: '/',
    },

    checkoutRoute: {
      type: String,
      default: 'checkout',
    },

    queryParams: {
      type: String,
      default: '',
    },

    queryObject: {
      type: Object,
      default() {
        return {};
      },
    },

    disableHomeLink: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isBlockedTipShown: false,
      steps: [
        {
          // This is the URL not the name
          routeName: this.homeRoute,
          // First step cannot be undone - should at least be in progress
          state: 'in-progress',
        },
        {
          // This is the URL not the name
          routeName: this.searchRoute,
          state: 'undone',
        },
        {
          // This is the URL not the name
          routeName: this.extrasRoute,
          state: this.skipExtras ? 'blocked' : 'undone',
        },
        {
          routeName: this.checkoutRoute,
          state: 'undone',
        },
      ] as Step[],
    };
  },

  computed: {
    progressRouteNames(): string[] {
      return [
        this.homeRoute,
        this.searchRoute,
        this.extrasRoute,
        this.checkoutRoute,
      ];
    },
    colourScheme(): ColourScheme {
      const scheme = getAppVariable('colours.header.font');
      if (scheme === COLOUR_SCHEMES.LIGHT || scheme === COLOUR_SCHEMES.DARK) {
        return scheme;
      }
      return COLOUR_SCHEMES.DARK;
    },
    isTravelInsuranceHeroProduct() {
      return getAppHeroProduct('lounges') === 'travel_insurance'
    },
    stepTranslationsBase() {
      return this.isTravelInsuranceHeroProduct
        ? 'layouts.progressIndicatorTravelInsurance.steps'
        : 'layouts.progressIndicatorParkings.steps';
    },
    isStepRenamingFlag(): boolean {
      return getFeatureFlag(FEATURE_FLAGS.CRO_1077_PROGRESS_INDICATOR_STEP_RENAMING)
    }
  },

  watch: {
    '$i18n.locale': function() {
      this.changeStepsState();
    },
    $route: function(to: Route, from: Route): void {
      this.changeStepsState();

      this.clearSearchOnRouteChange(to.name, this.homeRoute, from.name, [
        this.homeRoute,
      ]);

      this.clearSearchOnRouteChange(to.name, this.searchRoute, from.name, [
        this.homeRoute,
        this.searchRoute,
      ]);
    },
    extrasRoute(route) {
      this.steps[2].routeName = route;
      this.changeStepsState();
    },
    skipExtras(to: Route): void {
      const step = this.steps.find(
        (step: Step) => step.routeName === this.extrasRoute
      );

      if (step) {
        if (to) {
          step.state = 'blocked';
        } else {
          step.state = 'undone';
        }
      }
      this.changeStepsState();
    },
  },

  created() {
    this.changeStepsState();
    if (this.isTravelInsuranceHeroProduct) {
      // delete step for extras if travel insurance product
      this.steps.splice(2, 1);
    }
  },

  methods: {
    ...mapActions(useSearchResultsStore, ['clearSearchResults']),

    /**
     * Responsible for updated steps indicator progress based on,
     * 1. If the current route exists in the routeNames props
     * 2. If we are dedicated wl then modifies the string to use
     * normal route naming convention
     * 3. Sets the state of the step
     */
    changeStepsState() {
      // calculates current route index and changes other steps state according
      // to current route's position in the array
      const getIndex = (target: string): number =>
        this.steps.findIndex((i) => i.routeName === target);

      const routeName = getFeatureFlag(FEATURE_FLAGS.CUS_2533_DEDICATED_BOOKING_FLOW)
        ? this.$route.name.replace('dedicated-wl-', '')
        : this.$route.name;

      const routeIndex: number = getIndex(routeName || '');

      for (const step of this.steps) {
        if (step.state === 'blocked') {
          continue;
        }
        const stepIndex = getIndex(step.routeName);
        if (stepIndex === routeIndex) {
          this.steps[stepIndex].state = 'in-progress';
        } else if (stepIndex < routeIndex) {
          this.steps[stepIndex].state = 'done';
        } else if (stepIndex > routeIndex) {
          this.steps[stepIndex].state = 'undone';
        }
      }
    },

    changeTipState(step: Step['state'], tip?: boolean) {
      const defaultTip =
        typeof tip === 'boolean' ? tip : !this.isBlockedTipShown;
      this.isBlockedTipShown = step === 'blocked' && defaultTip;
    },

    isStepClickable(step: Step): boolean {
      if (step.state !== 'done') return false;
      if (isLoungePortal()) return false;
      if (this.disableHomeLink && step.routeName === this.homeRoute) return false;
      return true;
    },

    navigateTo(step: Step) {
      if (
        step.state !== 'blocked' &&
        !this.navigatingForward(this.$route.name, step.routeName) &&
        this.isStepClickable(step)
      ) {
        if (step.routeName.includes('http')) {
          const route = this.queryParams
            ? `${step.routeName}?${this.queryParams}`
            : step.routeName;
          window.location.assign(route);
        } else {
          const route = {
            name: step.routeName,
            ...(this.queryObject && { query: this.queryObject }),
          };
          this.$router.push(route);
        }
      }
    },

    // Going to (specified)earlier page from latter pages,
    // which are filtered from all based on (specified)earlier+current pages,
    // will result in clearing search data
    clearSearchOnRouteChange(
      to: Route['name'],
      page: string,
      from: Route['name'],
      pages: string[]
    ) {
      const filterRoutes = this.progressRouteNames.filter(
        (routeName) => !pages.includes(routeName)
      );
      if (to === page && filterRoutes.includes(from || '')) {
        this.clearSearchResults();
      }
    },

    showBlockedTip(step: Step) {
      return step.state === 'blocked' && this.isBlockedTipShown;
    },

    navigatingForward(from: Route['name'], to: Step['routeName']) {
      const getIndex = (target: Route['name']) =>
        this.steps.findIndex((i) => i.routeName === target);
      return getIndex(from) < getIndex(to);
    },

    keydownSpaceHandler(e: KeyboardEvent) {
      if (e.target instanceof HTMLElement) {
        e.target.click()
      }
    },
  },
});
